import React, { useEffect, useState } from 'react';
import Spinner from '../../ui/Spinner/Spinner';
import CheckMarkIcon from '../../ui/icons/CheckMark/CheckMark';
import PanelContentHeader from '../../ui/PanelContentHeader/PanelContentHeader';
import TextLink, { TextLinkTargetType } from '../../ui/TextLink/TextLink';
import PanelHeader from '../../ui/PanelHeader/PanelHeader';
import Panel from '../../ui/Panel/Panel';
import { LayoutTree } from '../DynamicLayout';
import { useTheme } from '../../../hooks/useTheme';
import { useSettings } from '../../../hooks/useSettings';
import { useTranslations } from '../../../hooks/useTranslations';
import {
  useAnalyticsPost,
  NarvarEventTypes,
  NarvarEventComponents,
  NarvarClickSubTypes,
  NarvarClickTypes,
} from '../../../hooks/Analytics';
import { usePageContext } from '../../../hooks/PageContext/usePageContext';
import LandscapeExpanded from './LandscapeExpanded';
import LandscapeCollapsed from './LandscapeCollapsed';
import Portrait from './Portrait';
import styles from './Feedback.module.css';

export { Portrait, LandscapeCollapsed, LandscapeExpanded };

const { FeatureLayout } = LayoutTree;

const localStorage = (() => {
  if (typeof window === 'undefined') return undefined;
  try {
    // reading the localStorage property can throw if we're in an iframe
    // see: https://stackoverflow.com/questions/67645164
    return window.localStorage;
  } catch (err) {
    console.error('Error reading localStorage', err);
    return undefined;
  }
})();

export interface FeedbackProps {
  featureName?: string;
  errorCallback?: (err: unknown) => void;
  layout?: LayoutTree.FeatureLayout;
  fixedPanelHeight?: string;
}

const Feedback = ({
  featureName,
  errorCallback,
  layout = FeatureLayout.PORTRAIT,
  fixedPanelHeight,
}: FeedbackProps) => {
  const {
    state: { trackingNumbers },
  } = usePageContext();

  const trackingNumber = trackingNumbers && trackingNumbers[0];

  const {
    feedbackCustomerSupportLink = '',
    feedbackPanelTitleText = '',
    feedbackSuccessHeaderText = '',
    feedbackSuccessBodyText = '',
  } = useTranslations();

  const {
    panelcontainerNoneNone: containerStyle,
    panelbodyNoneNone: panelBodyStyle,
    spinnerPrimaryNone: spinnerStyle,
    linksNoneDefault: textLinkStyle,
    linksNoneHover: textLinkHoverStyle,
  } = useTheme();

  const { feedback } = useSettings();

  const {
    enableInitialCollapse: initialCollapsed,
    customerSupportLink,
    enablePanelHeader: enablePanelHeaderIn,
    commentOptional,
  } = feedback ?? {};

  const enablePanelHeader =
    enablePanelHeaderIn === null ||
    enablePanelHeaderIn === undefined ||
    enablePanelHeaderIn;

  const isLandscape = layout === FeatureLayout.LANDSCAPE;

  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);
  const [selectedRating, setSelectedRating] = useState(0);

  const logEvent = useAnalyticsPost();

  useEffect(() => {
    logEvent({
      eventComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventFeature: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventType: NarvarEventTypes.COMPONENT_RENDER,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      trackLayout: layout,
    });
  }, [featureName, layout, logEvent]);

  // TODO: replace this with query to TBD service that stores
  // feedback submitted based on trackingNumber, etc.
  // isLoaded will be set for realz after this query as well
  const isLoaded = true;

  useEffect(() => {
    if (localStorage && trackingNumber) {
      try {
        const feedbackStorage = localStorage.getItem(
          `feedback_${trackingNumber}`,
        );
        if (feedbackStorage) setFeedbackSubmitted(true);
      } catch (err) {
        if (errorCallback) errorCallback(err);
      }
    }
  }, [trackingNumber, errorCallback]);

  const submitFeedback = (feedbackComment: string) => {
    logEvent({
      clickCategory: NarvarClickSubTypes.SURVEY_SUBMIT,
      clickSubType: NarvarClickSubTypes.SURVEY_SUBMIT,
      clickSurveyComment: feedbackComment,
      clickSurveyRating: selectedRating,
      clickType: NarvarClickTypes.INTERACTION,
      eventComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventFeature: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      trackLayout: layout,
    });

    // TODO: replace this with call to set this value in backend service
    if (localStorage && trackingNumber) {
      try {
        localStorage.setItem(`feedback_${trackingNumber}`, 'submittedFeedback');
      } catch (err) {
        if (errorCallback) errorCallback(err);
      }
    }
    setFeedbackSubmitted(true);
  };

  const onClickSupportLink = () => {
    logEvent({
      clickCategory: NarvarClickSubTypes.EXTERNAL_HELP_LINK,
      clickLinkDestinationUrl: customerSupportLink,
      clickSubType: NarvarClickSubTypes.EXTERNAL_HELP_LINK,
      clickType: NarvarClickTypes.OUTGOING,
      eventComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventFeature: NarvarEventComponents.NTH_PACK_FEEDBACK,
      eventType: NarvarEventTypes.CLICK,
      feature: featureName,
      pageComponent: NarvarEventComponents.NTH_PACK_FEEDBACK,
      trackLayout: layout,
    });
  };

  return (
    <Panel
      header={
        (enablePanelHeader || layout === FeatureLayout.PORTRAIT) && (
          <PanelHeader>{feedbackPanelTitleText}</PanelHeader>
        )
      }
      sOuter={{
        boxSizing: 'border-box',
        textAlign: isLandscape ? 'left' : 'center',
        overflowX: 'hidden',
        ...containerStyle,
        height:
          fixedPanelHeight ||
          (containerStyle && containerStyle.height) ||
          '100%',
      }}
      sInner={{
        margin: '0 auto',
        maxWidth: !isLandscape ? '19.25rem' : 'auto',
        overflowX: 'hidden',
        ...panelBodyStyle,
        height: fixedPanelHeight ? 'auto' : '100%',
      }}
    >
      {(() => {
        if (!isLoaded) return <Spinner s={spinnerStyle} />;

        if (feedbackSubmitted) {
          return (
            <div className={styles.submittedContainer}>
              <div className={styles.submittedContent}>
                <PanelContentHeader
                  as={enablePanelHeader ? 'p' : 'h2'}
                  featureName={featureName}
                  headerTextRole="status"
                  icon={<CheckMarkIcon />}
                  headerText={feedbackSuccessHeaderText ?? ''}
                  paragraphChildren={
                    <>
                      <div>{feedbackSuccessBodyText}</div>
                      {customerSupportLink && (
                        <div className={styles.csLink}>
                          <TextLink
                            target={TextLinkTargetType.BLANK}
                            href={customerSupportLink}
                            linkStyle={{
                              sDefault: textLinkStyle,
                              sHover: textLinkHoverStyle,
                            }}
                            onClick={() => onClickSupportLink()}
                          >
                            {feedbackCustomerSupportLink}
                          </TextLink>
                        </div>
                      )}
                    </>
                  }
                  isLandscape={isLandscape}
                />
              </div>
            </div>
          );
        }
        if (isLandscape && initialCollapsed) {
          return (
            <LandscapeCollapsed
              featureName={featureName}
              selectedRating={selectedRating}
              onRatingSelect={setSelectedRating}
              submitFeedback={submitFeedback}
              commentOptional={commentOptional ?? false}
              enablePanelHeader={enablePanelHeader}
            />
          );
        }
        if (isLandscape && !initialCollapsed) {
          return (
            <LandscapeExpanded
              featureName={featureName}
              selectedRating={selectedRating}
              onRatingSelect={setSelectedRating}
              onClickSupportLink={onClickSupportLink}
              submitFeedback={submitFeedback}
              commentOptional={commentOptional ?? false}
              enablePanelHeader={enablePanelHeader}
            />
          );
        }
        return (
          <Portrait
            featureName={featureName}
            selectedRating={selectedRating}
            onRatingSelect={setSelectedRating}
            onClickSupportLink={onClickSupportLink}
            submitFeedback={submitFeedback}
            commentOptional={commentOptional ?? false}
          />
        );
      })()}
    </Panel>
  );
};

export default Feedback;
